import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MdSettings, MdKeyboardArrowDown, MdKeyboardArrowUp, MdRadioButtonUnchecked, MdSupervisorAccount } from 'react-icons/md';
import { MdDashboard, MdPerson, MdDevices, MdBusiness, MdPayment, MdReceipt, MdAttachMoney, MdVerifiedUser, MdSwapHoriz, MdList, MdPhoneAndroid, MdHistory } from 'react-icons/md';
import { API_BASE_URL, createApiConfig } from '../config/api';

const Sidebar = ({ selectedTable, setSelectedTable, setPage, tables, user, searchTerm, setSearchTerm, deviceSearchTerm, setDeviceSearchTerm, paymentMethodSearchTerm, setPaymentMethodSearchTerm, receiptSearchTerm, setReceiptSearchTerm, revenueItemSearchTerm, setRevenueItemSearchTerm, ticketValidationSearchTerm, setTicketValidationSearchTerm, transactionSearchTerm, setTransactionSearchTerm, ussdSessionSearchTerm, setUssdSessionSearchTerm, auditLogSearchTerm, setAuditLogSearchTerm }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [visibleTables, setVisibleTables] = useState(tables);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const handleDropdownClick = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  // Fetch sidebar permissions and filter tables
  useEffect(() => {
    const fetchSidebarPermissions = async () => {
      try {
        // Try to get user-specific permissions first, fallback to role-based
        let response;
        if (user && user.user_id) {
          try {
            response = await axios.get(`${API_BASE_URL}/sidebar-permissions/user/${user.user_id}/visible?roleName=${user.role_name}`, createApiConfig());
          } catch (err) {
            // Fallback to role-based permissions if user-specific fail
            response = await axios.get(`${API_BASE_URL}/sidebar-permissions/role/super_admin/visible`, createApiConfig());
          }
        } else {
          response = await axios.get(`${API_BASE_URL}/sidebar-permissions/role/super_admin/visible`, createApiConfig());
        }
        const permissions = response.data;

        // Create a map of visible menu items
        const visibleMenuKeys = new Set(permissions.map(p => p.menu_item_key));

        // Filter tables based on permissions
        const filteredTables = tables.filter(table => {
          // Map table names to permission keys
          const tableKey = getTablePermissionKey(table.name);
          return visibleMenuKeys.has(tableKey);
        }).map(table => {
          // Filter subItems if they exist
          if (table.subItems) {
            const filteredSubItems = table.subItems.filter(subItem => {
              const subItemKey = getTablePermissionKey(subItem.name);
              return visibleMenuKeys.has(subItemKey);
            });
            return { ...table, subItems: filteredSubItems };
          }
          return table;
        });

        setVisibleTables(filteredTables);
        setPermissionsLoaded(true);
      } catch (error) {
        console.error('Failed to fetch sidebar permissions:', error);
        // Fallback to showing all tables if permissions fetch fails
        setVisibleTables(tables);
        setPermissionsLoaded(true);
      }
    };

    if (tables && tables.length > 0 && user) {
      fetchSidebarPermissions();
    }
  }, [tables, user]);

  // Map table names to permission keys
  const getTablePermissionKey = (tableName) => {
    const keyMap = {
      'Dashboard': 'dashboard',
      'Users': 'users',
      'Manage MMDAs': 'manage_mmdas',
      'MMDA List': 'mmda_list',
      'MMDA Zones': 'mmda_zones',
      'Groups': 'groups',
      'Revenue Officers': 'revenue_officers',
      'Traders': 'traders',
      'Payment Methods': 'payment_methods',
      'Receipts': 'receipts',
      'Ticket Validations': 'ticket_validations',
      'Transactions': 'transactions',
      'USSD Sessions': 'ussd_sessions',
      'Audit Logs': 'audit_logs',
      'Settings': 'settings',
      'Devices': 'devices',
      'Revenue Items': 'revenue_items'
    };
    return keyMap[tableName] || tableName.toLowerCase().replace(/\s+/g, '_');
  };

  // Don't render until permissions are loaded
  if (!permissionsLoaded) {
    return (
      <div style={{
        width: 280,
        backgroundColor: '#1976d2',
        color: 'white',
        padding: '20px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px'
      }}>
        <div style={{ textAlign: 'center', opacity: 0.8 }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      width: 280,
      backgroundColor: '#1976d2',
      color: 'white',
      padding: '20px 0',
      overflowY: 'auto',
      boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
      minWidth: 280
    }}>
      {/* SuperAdmin Header */}
      <div style={{
        padding: '0 24px 20px',
        borderBottom: '1px solid rgba(255,255,255,0.2)',
        marginBottom: 20
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <MdSupervisorAccount size={28} style={{ color: '#fff' }} />
          <h3 style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 'bold',
            color: '#fff',
            letterSpacing: '0.5px'
          }}>
            SuperAdmin
          </h3>
        </div>
        <p style={{
          margin: '8px 0 0',
          fontSize: 13,
          opacity: 0.9,
          color: '#e3f2fd',
          lineHeight: 1.4
        }}>
          System-wide Management
        </p>
        <p style={{
          margin: '4px 0 0',
          fontSize: 12,
          opacity: 0.8,
          color: '#bbdefb',
          lineHeight: 1.3
        }}>
          Welcome, {user?.username || user?.full_name || 'Administrator'}
        </p>
      </div>

      {/* Navigation Items */}
      <nav style={{
        padding: '0 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4
      }}>
        {visibleTables.map(t => (
          <div key={t.name} style={{ marginBottom: 4 }}>
            <button
              onClick={() => {
                if (t.subItems) {
                  handleDropdownClick(t.name);
                } else {
                  setSelectedTable(t);
                  setPage && setPage(1);
                  setSearchTerm && setSearchTerm('');
                  setDeviceSearchTerm && setDeviceSearchTerm('');
                  setPaymentMethodSearchTerm && setPaymentMethodSearchTerm('');
                  setReceiptSearchTerm && setReceiptSearchTerm('');
                  setRevenueItemSearchTerm && setRevenueItemSearchTerm('');
                  setTicketValidationSearchTerm && setTicketValidationSearchTerm('');
                  setTransactionSearchTerm && setTransactionSearchTerm('');
                  setUssdSessionSearchTerm && setUssdSessionSearchTerm('');
                  setAuditLogSearchTerm && setAuditLogSearchTerm('');
                }
              }}
              style={{
                width: '100%',
                backgroundColor: selectedTable.name === t.name ? 'rgba(255,255,255,0.15)' : 'transparent',
                border: 'none',
                color: '#fff',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 14,
                fontWeight: selectedTable.name === t.name ? '600' : '500',
                transition: 'all 0.2s ease',
                textAlign: 'left',
                outline: 'none',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={e => {
                if (selectedTable.name !== t.name) {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                }
              }}
              onMouseLeave={e => {
                if (selectedTable.name !== t.name) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 18,
                  opacity: selectedTable.name === t.name ? 1 : 0.9
                }}>
                  {t.icon}
                </span>
                <span style={{
                  fontSize: 14,
                  fontWeight: selectedTable.name === t.name ? '600' : '500',
                  letterSpacing: '0.3px'
                }}>
                  {t.name}
                </span>
              </div>
              {t.subItems && (
                <span style={{
                  opacity: 0.8,
                  transition: 'transform 0.2s ease',
                  transform: openDropdown === t.name ? 'rotate(180deg)' : 'rotate(0deg)'
                }}>
                  <MdKeyboardArrowDown size={18} />
                </span>
              )}
              {selectedTable.name === t.name && (
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  backgroundColor: '#fff',
                  borderRadius: '0 2px 2px 0'
                }} />
              )}
            </button>

            {t.subItems && openDropdown === t.name && (
              <div style={{
                marginTop: 8,
                marginLeft: 16,
                paddingLeft: 16,
                borderLeft: '2px solid rgba(255,255,255,0.2)',
                animation: 'slideDown 0.2s ease'
              }}>
                {t.subItems.map(subItem => (
                  <button
                    key={subItem.name}
                    onClick={() => {
                      setSelectedTable(subItem);
                      setPage && setPage(1);
                    }}
                    style={{
                      width: '100%',
                      backgroundColor: selectedTable.name === subItem.name ? 'rgba(255,255,255,0.2)' : 'transparent',
                      border: 'none',
                      color: selectedTable.name === subItem.name ? '#fff' : 'rgba(255,255,255,0.9)',
                      padding: '10px 12px',
                      marginBottom: 4,
                      borderRadius: 6,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      fontSize: 13,
                      fontWeight: selectedTable.name === subItem.name ? '600' : '400',
                      textAlign: 'left',
                      transition: 'all 0.2s ease',
                      outline: 'none'
                    }}
                    onMouseEnter={e => {
                      if (selectedTable.name !== subItem.name) {
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                        e.currentTarget.style.color = '#fff';
                      }
                    }}
                    onMouseLeave={e => {
                      if (selectedTable.name !== subItem.name) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'rgba(255,255,255,0.9)';
                      }
                    }}
                  >
                    <MdRadioButtonUnchecked size={14} style={{ opacity: 0.7 }} />
                    <span style={{ letterSpacing: '0.2px' }}>{subItem.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
            color: blue;
          }
        }
      `}</style>
    </div>
  );
};

export default Sidebar;